import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, Loader2, Plus, X } from "lucide-react";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CONTACT_METHODS = [
  { value: "email", label: "Email" },
  { value: "phone", label: "Phone" },
  { value: "text", label: "Text" },
];

const HEAR_ABOUT = [
  { value: "social-media", label: "Social media" },
  { value: "friend-family", label: "Friend or family" },
  { value: "school", label: "School" },
  { value: "media", label: "Media" },
  { value: "internet-search", label: "Internet search" },
  { value: "other", label: "Other" },
];

const YEAR_LEVELS = [7, 8, 9, 10];

const emptyChild = { first_name: "", last_name: "", year_level: "7", current_school: "" };

const inputCls =
  "w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 outline-none transition-colors duration-300 focus:border-gold focus:ring-2 focus:ring-gold/20";
const labelCls = "mb-2 block font-mono text-[10px] tracking-[0.25em] text-slate-500";
const sectionCls = "sm:col-span-2 mt-2 border-t border-slate-200 pt-6";
const sectionTitleCls = "font-mono text-xs uppercase tracking-[0.3em] text-gold";

const Field = ({ label, required, children }) => (
  <div>
    <label className={labelCls}>
      {label} {required && "*"}
    </label>
    {children}
  </div>
);

export default function ExpressionOfInterest() {
  const [form, setForm] = useState({
    parent_first_name: "",
    parent_last_name: "",
    email: "",
    mobile: "",
    suburb: "",
    preferred_contact: "email",
    children: [{ ...emptyChild }],
    hear_about: "social-media",
    info_evening: "yes",
    comments: "",
    consent_contact: false,
    consent_news: false,
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setChild = (i, k) => (e) =>
    setForm((f) => ({
      ...f,
      children: f.children.map((c, idx) => (idx === i ? { ...c, [k]: e.target.value } : c)),
    }));

  const addChild = () => setForm((f) => ({ ...f, children: [...f.children, { ...emptyChild }] }));
  const removeChild = () => setForm((f) => ({ ...f, children: [f.children[0]] }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent_contact) {
      toast.error("Consent required", {
        description: "Please agree to VPS contacting you about enrolment.",
      });
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        info_evening: form.info_evening === "yes",
        comments: form.comments || null,
        children: form.children.map((c) => ({
          ...c,
          year_level: Number(c.year_level),
          current_school: c.current_school || null,
        })),
      };
      await axios.post(`${API}/eoi`, payload);
      setSubmitted(true);
      toast.success("Expression of interest received", {
        description: "Thank you — VPS will be in touch.",
      });
    } catch (err) {
      if (err.response?.status === 409) {
        toast.info("You are already on the list", { description: "This email has already registered interest." });
      } else {
        toast.error("Something went wrong", { description: "Please check your details and try again." });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="relative overflow-hidden pb-20 pt-14 lg:pt-20">
      <div className="pointer-events-none absolute -top-24 right-1/4 h-[24rem] w-[24rem] rounded-full bg-gold/10 blur-[110px]" />

      <div className="relative z-10 mx-auto grid w-full max-w-6xl grid-cols-1 gap-12 px-4 sm:px-6 lg:grid-cols-12 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="flex flex-col justify-center lg:col-span-5"
        >
          <Link
            to="/"
            data-testid="eoi-back-link"
            className="mb-8 flex w-fit items-center gap-2 font-mono text-[11px] tracking-[0.2em] text-slate-500 transition-colors duration-300 hover:text-gold"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> BACK TO SITE
          </Link>
          <p className="mb-5 font-mono text-xs uppercase tracking-[0.3em] text-gold">
            Expressions of Interest — 2027 Enrolment
          </p>
          <h1 className="font-serif text-3xl font-bold leading-[1.05] tracking-tight text-navy sm:text-5xl">
            Register your family's interest
          </h1>
          <p className="mt-6 max-w-sm text-sm sm:text-base leading-relaxed text-slate-600">
            Complete the form to register your interest in enrolment at VPS
            for 2027. Our enrolments team will contact you with next steps,
            including information evenings.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-7"
        >
          <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-xl sm:p-10">
            {submitted ? (
              <div data-testid="eoi-success-message" className="flex flex-col items-start gap-4 py-10">
                <CheckCircle2 className="h-10 w-10 text-gold" />
                <h2 className="font-serif text-2xl font-semibold text-navy">Thank you — you're registered.</h2>
                <p className="max-w-md text-sm leading-relaxed text-slate-600">
                  Your expression of interest has been received by VPS. Our
                  enrolments team will be in touch using your preferred contact
                  method.
                </p>
                <Link
                  to="/"
                  className="mt-4 font-mono text-[11px] tracking-[0.2em] text-gold transition-colors duration-300 hover:text-gold-hover"
                >
                  RETURN TO THE SITE
                </Link>
              </div>
            ) : (
              <form onSubmit={submit} className="grid grid-cols-1 gap-6 sm:grid-cols-2" data-testid="eoi-form">
                <div className={`${sectionCls} !mt-0 !border-t-0 !pt-0`}>
                  <p className={sectionTitleCls}>Parent or caregiver</p>
                </div>
                <Field label="FIRST NAME" required>
                  <input data-testid="eoi-parent-first-name-input" required value={form.parent_first_name} onChange={set("parent_first_name")} placeholder="Jane" className={inputCls} />
                </Field>
                <Field label="LAST NAME" required>
                  <input data-testid="eoi-parent-last-name-input" required value={form.parent_last_name} onChange={set("parent_last_name")} placeholder="Citizen" className={inputCls} />
                </Field>
                <Field label="EMAIL ADDRESS" required>
                  <input data-testid="eoi-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="jane@example.com" className={inputCls} />
                </Field>
                <Field label="MOBILE NUMBER" required>
                  <input data-testid="eoi-mobile-input" required type="tel" value={form.mobile} onChange={set("mobile")} placeholder="021 000 000" className={inputCls} />
                </Field>
                <Field label="SUBURB" required>
                  <input data-testid="eoi-suburb-input" required value={form.suburb} onChange={set("suburb")} placeholder="Your suburb" className={inputCls} />
                </Field>
                <Field label="PREFERRED CONTACT METHOD" required>
                  <select data-testid="eoi-contact-method-select" value={form.preferred_contact} onChange={set("preferred_contact")} className={`${inputCls} appearance-none`}>
                    {CONTACT_METHODS.map((o) => (
                      <option key={o.value} value={o.value} className="bg-white">{o.label}</option>
                    ))}
                  </select>
                </Field>

                {form.children.map((child, i) => (
                  <div key={i} className="sm:col-span-2 contents">
                    <div className={`${sectionCls} flex items-center justify-between`}>
                      <p className={sectionTitleCls}>
                        Child {form.children.length > 1 ? i + 1 : ""}
                      </p>
                      {i === 1 && (
                        <button
                          type="button"
                          data-testid="eoi-remove-child-button"
                          onClick={removeChild}
                          className="flex items-center gap-1 font-mono text-[10px] tracking-[0.2em] text-slate-400 transition-colors duration-300 hover:text-red-600"
                        >
                          <X className="h-3 w-3" /> REMOVE
                        </button>
                      )}
                    </div>
                    <Field label="FIRST NAME" required>
                      <input data-testid={`eoi-child-${i + 1}-first-name-input`} required value={child.first_name} onChange={setChild(i, "first_name")} placeholder="Child's first name" className={inputCls} />
                    </Field>
                    <Field label="LAST NAME" required>
                      <input data-testid={`eoi-child-${i + 1}-last-name-input`} required value={child.last_name} onChange={setChild(i, "last_name")} placeholder="Child's last name" className={inputCls} />
                    </Field>
                    <Field label="YEAR LEVEL IN 2027" required>
                      <select data-testid={`eoi-child-${i + 1}-year-level-select`} value={child.year_level} onChange={setChild(i, "year_level")} className={`${inputCls} appearance-none`}>
                        {YEAR_LEVELS.map((y) => (
                          <option key={y} value={y} className="bg-white">Year {y}</option>
                        ))}
                      </select>
                    </Field>
                    <Field label="CURRENT SCHOOL">
                      <input data-testid={`eoi-child-${i + 1}-current-school-input`} value={child.current_school} onChange={setChild(i, "current_school")} placeholder="Current school" className={inputCls} />
                    </Field>
                  </div>
                ))}

                {form.children.length === 1 && (
                  <div className="sm:col-span-2">
                    <button
                      type="button"
                      data-testid="eoi-add-child-button"
                      onClick={addChild}
                      className="flex items-center gap-2 rounded-full border border-gold/50 px-5 py-2.5 font-mono text-[11px] font-semibold tracking-[0.15em] text-gold-hover transition-colors duration-300 hover:bg-gold/10"
                    >
                      <Plus className="h-3.5 w-3.5" /> ADD A SECOND CHILD
                    </button>
                  </div>
                )}

                <div className={sectionCls}>
                  <p className={sectionTitleCls}>About your interest</p>
                </div>
                <Field label="HOW DID YOU HEAR ABOUT US?" required>
                  <select data-testid="eoi-hear-about-select" value={form.hear_about} onChange={set("hear_about")} className={`${inputCls} appearance-none`}>
                    {HEAR_ABOUT.map((o) => (
                      <option key={o.value} value={o.value} className="bg-white">{o.label}</option>
                    ))}
                  </select>
                </Field>
                <Field label="ATTEND AN INFORMATION EVENING?" required>
                  <div className="flex gap-3 pt-1">
                    {["yes", "no"].map((v) => (
                      <label key={v} className="flex cursor-pointer items-center gap-2 rounded-full border border-slate-300 px-5 py-2.5 text-sm text-slate-600 transition-colors duration-300 has-[:checked]:border-gold has-[:checked]:bg-gold/5 has-[:checked]:text-gold-hover">
                        <input
                          type="radio"
                          name="info_evening"
                          data-testid={`eoi-info-evening-${v}`}
                          value={v}
                          checked={form.info_evening === v}
                          onChange={set("info_evening")}
                          className="accent-[#C9A227]"
                        />
                        {v === "yes" ? "Yes" : "No"}
                      </label>
                    ))}
                  </div>
                </Field>
                <div className="sm:col-span-2">
                  <Field label="QUESTIONS OR COMMENTS">
                    <textarea
                      data-testid="eoi-comments-input"
                      rows={4}
                      value={form.comments}
                      onChange={set("comments")}
                      placeholder="Anything you'd like to ask or tell us…"
                      className={`${inputCls} resize-none`}
                    />
                  </Field>
                  <p className="mt-2 font-mono text-[10px] tracking-[0.1em] text-slate-400">
                    PLEASE DO NOT INCLUDE HEALTH OR OTHER PERSONAL DETAILS IN THIS FIELD.
                  </p>
                </div>

                <div className={sectionCls}>
                  <p className={sectionTitleCls}>Consents</p>
                </div>
                <div className="sm:col-span-2 flex flex-col gap-4">
                  <label className="flex cursor-pointer items-start gap-3 text-sm leading-relaxed text-slate-700">
                    <input
                      type="checkbox"
                      data-testid="eoi-consent-contact-checkbox"
                      checked={form.consent_contact}
                      onChange={(e) => setForm((f) => ({ ...f, consent_contact: e.target.checked }))}
                      className="mt-1 h-4 w-4 accent-[#C9A227]"
                    />
                    <span>
                      I agree to VPS contacting me about my child's enrolment.{" "}
                      <span className="text-gold">*</span>
                    </span>
                  </label>
                  <label className="flex cursor-pointer items-start gap-3 text-sm leading-relaxed text-slate-600">
                    <input
                      type="checkbox"
                      data-testid="eoi-consent-news-checkbox"
                      checked={form.consent_news}
                      onChange={(e) => setForm((f) => ({ ...f, consent_news: e.target.checked }))}
                      className="mt-1 h-4 w-4 accent-[#C9A227]"
                    />
                    <span>I would like to receive news and updates from VPS. <span className="text-slate-400">(optional)</span></span>
                  </label>
                </div>

                <div className="sm:col-span-2">
                  <p data-testid="eoi-privacy-statement" className="text-xs leading-relaxed text-slate-500">
                    Vanguard Prep School Limited collects the information in this
                    form to respond to your interest in enrolling your child, to
                    contact you about enrolment and information events and, if you
                    agree, to send you news about the School. Your information will
                    be held securely by Vanguard Prep School Limited and used only
                    by our staff involved in enrolment and our service providers,
                    including Advance Training Group Limited, which provides
                    administrative services to the School. We will not share it with
                    anyone else without your consent unless the law requires us to.
                    Providing this information is voluntary, but without it we may
                    not be able to respond to your enquiry. You can ask to see or
                    correct your information at any time by contacting our Privacy
                    Officer at privacy@vanguardprep.school.nz.
                  </p>
                  <a
                    href="/privacy-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="eoi-privacy-policy-link"
                    className="mt-3 inline-block font-mono text-[10px] tracking-[0.2em] text-gold-hover underline underline-offset-4 transition-colors duration-300 hover:text-gold"
                  >
                    READ OUR FULL PRIVACY POLICY
                  </a>
                </div>

                <div className="sm:col-span-2">
                  <button
                    type="submit"
                    data-testid="eoi-form-submit-button"
                    disabled={loading}
                    className="flex w-full items-center justify-center gap-2 rounded-full bg-gold px-8 py-4 text-sm font-semibold text-black transition-all duration-200 hover:bg-gold-hover active:scale-95 disabled:opacity-60"
                  >
                    {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                    {loading ? "Submitting…" : "Submit Expression of Interest"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </motion.div>
      </div>
    </main>
  );
}
